import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../api/auth';
import { addReviewResults } from '../api/learning-cards';
import { LEARNING_CARDS_ROUTES } from '../constants/learning-cards';
import TextWithReading from '../components/TextWithReading';
import { extractReadingPairs } from '../utils/cardUtils';

function TestPage() {
    const { groupId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [authenticated] = useState(isAuthenticated());
    const [cards, setCards] = useState([]);
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [showAnswer, setShowAnswer] = useState(false);
    const [results, setResults] = useState([]);
    const [testCompleted, setTestCompleted] = useState(false);
    const [submittingResults, setSubmittingResults] = useState(false);
    const [submissionError, setSubmissionError] = useState('');

    // Refs for timing
    const cardStartTime = useRef(null);
    const answerShownTime = useRef(null);

    // Helper function to extract card texts and readings based on display mode
    const getCardTextsAndReadings = (card, displayMode, groupCardSideTypes) => {
        let side1Text = '';
        let side2Text = '';
        let side1Readings = [];
        let side2Readings = [];

        if (card.sides && Array.isArray(card.sides) && card.sides.length >= 2) {
            side1Text = card.sides[0]?.text || '';
            side2Text = card.sides[1]?.text || '';
            side1Readings = extractReadingPairs(card.sides[0]);
            side2Readings = extractReadingPairs(card.sides[1]);
        } else {
            side1Text = card.front_text || card.frontText || '';
            side2Text = card.back_text || card.backText || '';
            side1Readings = [];
            side2Readings = [];
        }

        // If displayMode is SIDE_2_AS_FRONT, swap the sides
        if (displayMode === 'SIDE_2_AS_FRONT') {
            return {
                frontText: side2Text,
                backText: side1Text,
                frontReadings: side2Readings,
                backReadings: side1Readings
            };
        }

        return {
            frontText: side1Text,
            backText: side2Text,
            frontReadings: side1Readings,
            backReadings: side2Readings
        };
    };

    useEffect(() => {
        if (location.state?.reviewCards) {
            console.log('Review cards received:', location.state.reviewCards);
            setCards(location.state.reviewCards);
            cardStartTime.current = Date.now();
        } else {
            navigate(`/learning-cards/groups/${groupId}`);
        }
    }, [location.state, groupId, navigate]);

    const handleShowAnswer = () => {
        setShowAnswer(true);
        answerShownTime.current = Date.now();
    };

    const handleAnswer = (confidence) => {
        // Calculate duration in milliseconds then convert to seconds
        const endTime = Date.now();
        const durationMs = endTime - cardStartTime.current;
        const durationSeconds = Math.floor(durationMs / 1000);

        const newResult = {
            card_id: parseInt(cards[currentCardIndex].id),
            card_answer: confidence,
            duration: `${durationSeconds}s`
        };

        const updatedResults = [...results, newResult];
        setResults(updatedResults);

        if (currentCardIndex < cards.length - 1) {
            setCurrentCardIndex(currentCardIndex + 1);
            setShowAnswer(false);
            cardStartTime.current = Date.now();
            answerShownTime.current = null;
        } else {
            setTestCompleted(true);
            submitResults(updatedResults);
        }
    };

    const submitResults = async (testResults) => {
        setSubmittingResults(true);
        setSubmissionError('');

        console.log('Submitting test results:', testResults);

        try {
            const result = await addReviewResults(groupId, testResults);

            if (!result.success) {
                setSubmissionError(result.message || 'Failed to submit test results');
            }
        } catch (err) {
            console.error('Error submitting test results:', err);
            setSubmissionError('An error occurred while submitting results');
        } finally {
            setSubmittingResults(false);
        }
    };

    const handleFinishTest = () => {
        navigate(`/learning-cards/groups/${groupId}`);
    };

    if (!authenticated) {
        return <Navigate to={LEARNING_CARDS_ROUTES.BASE} replace />;
    }

    if (cards.length === 0) {
        return (
            <div className="page-section">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (testCompleted) {
        return (
            <div className="page-section">
                <div className="card">
                    <div className="card-body text-center py-5">
                        <h2 className="mb-4">Test Completed!</h2>
                        <p className="mb-4">You've reviewed {results.length} cards.</p>

                        {submittingResults && (
                            <div className="mb-3">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Submitting results...</span>
                                </div>
                                <p className="mt-2">Saving your progress...</p>
                            </div>
                        )}

                        {submissionError && (
                            <div className="alert alert-warning mb-3" role="alert">
                                {submissionError}
                            </div>
                        )}

                        <button
                            className="btn btn-primary"
                            onClick={handleFinishTest}
                            disabled={submittingResults}
                        >
                            Back to Group
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const currentCard = cards[currentCardIndex];
    const displayMode = location.state?.displayMode || 'SIDE_1_AS_FRONT';
    const groupCardSideTypes = location.state?.groupCardSideTypes || [];

    const { frontText, backText, frontReadings, backReadings } = getCardTextsAndReadings(currentCard, displayMode, groupCardSideTypes);

    console.log('Current card readings:', { frontReadings, backReadings }); // Debug log

    return (
        <div className="page-section">
            <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                    <h4>Card {currentCardIndex + 1} of {cards.length}</h4>
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => navigate(`/learning-cards/groups/${groupId}`)}
                    >
                        Exit Test
                    </button>
                </div>
                <div className="progress mt-2">
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${((currentCardIndex + 1) / cards.length) * 100}%` }}
                        aria-valuenow={currentCardIndex + 1}
                        aria-valuemin="0"
                        aria-valuemax={cards.length}
                    ></div>
                </div>
            </div>

            <div className="card mb-4">
                <div className="card-body text-center py-5">
                    <div className="mb-4">
                        {frontReadings.length > 0 ? (
                            <TextWithReading
                                text={frontText}
                                readingPairs={frontReadings}
                                className="h3"
                            />
                        ) : (
                            <h3>{frontText || 'No front text'}</h3>
                        )}
                    </div>

                    {!showAnswer ? (
                        <button
                            className="btn btn-primary btn-lg"
                            onClick={handleShowAnswer}
                        >
                            Show Answer
                        </button>
                    ) : (
                        <>
                            <div className="alert alert-info mb-4">
                                {backReadings.length > 0 ? (
                                    <TextWithReading
                                        text={backText}
                                        readingPairs={backReadings}
                                        className="h4"
                                    />
                                ) : (
                                    <h4>{backText || 'No back text'}</h4>
                                )}
                            </div>

                            <p className="text-muted mb-3">How well did you know this?</p>

                            <div className="d-flex justify-content-center gap-2">
                                <button
                                    className="btn btn-danger"
                                    onClick={() => handleAnswer(3)}
                                >
                                    Fail
                                </button>
                                <button
                                    className="btn btn-warning"
                                    onClick={() => handleAnswer(2)}
                                >
                                    Hard
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={() => handleAnswer(1)}
                                >
                                    Good
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => handleAnswer(0)}
                                >
                                    Easy
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default TestPage;